import { cn } from '@/lib/utils';
import { t } from 'i18next';
import { useState, useEffect, FC, useCallback, useRef } from 'react';

type Props = {
	ownerId: string;
	initialDays: number;
	maxDays?: number;
	doFunc: (args: { ownerId: string; value: number }) => void;
};

const PermissionsListBookingLimitations: FC<Props> = ({
	ownerId,
	initialDays,
	maxDays = 365,
	doFunc,
}) => {
	const [days, setDays] = useState(0);
	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		setDays(initialDays);
	}, [initialDays]);

	const debouncedDoFunc = useCallback(
		(value: number) => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			if (value > maxDays) return;

			debounceTimeout.current = setTimeout(() => {
				doFunc({ ownerId, value });
			}, 1000);
		},
		[doFunc, ownerId],
	);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		setDays(value);
		debouncedDoFunc(value);
	};

	const isOverLimit = days >= maxDays;

	return (
		<div className='relative text-xs text-popover-800'>
			<input
				type='text'
				className={cn(
					'w-23 pl-3.5 pr-13 py-2 flex items-center gap-2.5 border border-popover-100 rounded-semi-xl bg-popover-10 focus:outline-primary-color',
					isOverLimit && 'outline-2 outline-error-1000',
				)}
				value={days ?? 0}
				onChange={handleChange}
				maxLength={3}
			/>
			<span className='absolute top-1/2 -translate-y-1/2 right-3.5'>
				{t(
					'main.settings.components.permissions.list.limits.component.text',
				)}
			</span>
			{isOverLimit && (
				<span className='absolute -bottom-5 text-xs text-error-1000'>
					*{' '}
					{t(
						'main.settings.components.permissions.list.limits.component.error',
					)}{' '}
					- {maxDays}{' '}
				</span>
			)}
		</div>
	);
};

export default PermissionsListBookingLimitations;
